/**
 * Mock Task Data for Development
 *
 * This data follows the FHIR Task resource specification.
 * In production, this would be fetched from Medplum using useSearchResources.
 */

import type { Task } from '@medplum/fhirtypes';

/**
 * Current user reference (mock)
 */
export const currentUserRef = 'Practitioner/current-user';
export const currentUserName = 'Dr. Sarah Johnson';

/**
 * Mock team members for assignment
 */
export const mockTeamMembers = [
  { id: 'current-user', name: 'Dr. Sarah Johnson', reference: 'Practitioner/current-user' },
  { id: 'practitioner-2', name: 'Dr. Michael Chen', reference: 'Practitioner/practitioner-2' },
  { id: 'practitioner-3', name: 'Jennifer Williams, LCSW', reference: 'Practitioner/practitioner-3' },
  { id: 'practitioner-4', name: 'Robert Martinez, LPC', reference: 'Practitioner/practitioner-4' },
  { id: 'practitioner-5', name: 'Dr. Emily Thompson', reference: 'Practitioner/practitioner-5' },
];

/**
 * Task type options for filtering (FHIR Task.code coding)
 */
export const TASK_TYPE_OPTIONS = [
  { value: 'missing-note', label: 'Missing Note' },
  { value: 'unsigned-note', label: 'Unsigned Note' },
  { value: 'cosign-needed', label: 'Co-sign Needed' },
  { value: 'missing-document', label: 'Missing Document' },
  { value: 'referral-request', label: 'Referral Request' },
  { value: 'signature', label: 'Signature Required' },
  { value: 'treatment-plan', label: 'Treatment Plan Review' },
  { value: 'intake-followup', label: 'Intake Follow-up' },
  { value: 'other', label: 'Other' },
];

/**
 * Mock patients (clients) for task association
 */
export const mockPatients = [
  { id: 'patient-1', name: 'John Doe', reference: 'Patient/patient-1' },
  { id: 'patient-2', name: 'Jane Smith', reference: 'Patient/patient-2' },
  { id: 'patient-3', name: 'Alex Johnson', reference: 'Patient/patient-3' },
  { id: 'patient-4', name: 'Maria Garcia', reference: 'Patient/patient-4' },
  { id: 'patient-5', name: 'David Wilson', reference: 'Patient/patient-5' },
];

/**
 * Helper to create a date relative to today
 */
function daysFromNow(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

/**
 * Mock tasks following FHIR Task specification.
 * Provider-focused: mostly missing notes, notes missing signatures, and occasional housekeeping.
 */
export const mockTasks: Task[] = [
  // Overdue — missing notes / signatures
  {
    resourceType: 'Task',
    id: 'task-1',
    status: 'in-progress',
    intent: 'order',
    priority: 'stat',
    description: 'Progress note missing — John Doe (session 2/12)',
    for: { reference: 'Patient/patient-1', display: 'John Doe' },
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(-5),
    restriction: {
      period: {
        end: daysFromNow(-2), // Overdue
      },
    },
    note: [{ text: 'Note due within 24 hours of session. Chart flagged for compliance.' }],
    code: { coding: [{ code: 'missing-note', display: 'Missing Note' }] },
  },
  {
    resourceType: 'Task',
    id: 'task-2',
    status: 'requested',
    intent: 'order',
    priority: 'asap',
    description: 'Sign progress note — Jane Smith (2/14)',
    for: { reference: 'Patient/patient-2', display: 'Jane Smith' },
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(-3),
    restriction: {
      period: {
        end: daysFromNow(-1), // Overdue
      },
    },
    code: { coding: [{ code: 'signature', display: 'Signature' }] },
  },

  // Due today
  {
    resourceType: 'Task',
    id: 'task-3',
    status: 'accepted',
    intent: 'order',
    priority: 'urgent',
    description: 'Progress note missing signature — Alex Johnson',
    for: { reference: 'Patient/patient-3', display: 'Alex Johnson' },
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(-1),
    restriction: {
      period: {
        end: daysFromNow(0), // Today
      },
    },
    code: { coding: [{ code: 'signature', display: 'Signature' }] },
  },
  {
    resourceType: 'Task',
    id: 'task-4',
    status: 'in-progress',
    intent: 'order',
    priority: 'routine',
    description: 'Document session note — John Doe (today\'s session)',
    for: { reference: 'Patient/patient-1', display: 'John Doe' },
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(0),
    restriction: {
      period: {
        end: daysFromNow(0), // Today
      },
    },
    code: { coding: [{ code: 'missing-note', display: 'Missing Note' }] },
  },

  // Due this week
  {
    resourceType: 'Task',
    id: 'task-5',
    status: 'requested',
    intent: 'order',
    priority: 'routine',
    description: 'Progress note missing — Maria Garcia (2/11)',
    for: { reference: 'Patient/patient-4', display: 'Maria Garcia' },
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(-2),
    restriction: {
      period: {
        end: daysFromNow(3),
      },
    },
    code: { coding: [{ code: 'missing-note', display: 'Missing Note' }] },
  },
  {
    resourceType: 'Task',
    id: 'task-6',
    status: 'accepted',
    intent: 'order',
    priority: 'urgent',
    description: 'Co-sign progress note — David Wilson (supervisee note)',
    for: { reference: 'Patient/patient-5', display: 'David Wilson' },
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(-1),
    restriction: {
      period: {
        end: daysFromNow(2),
      },
    },
    note: [{ text: 'Note submitted by Jennifer Williams, LCSW. Awaiting supervisor signature.' }],
    code: { coding: [{ code: 'signature', display: 'Signature' }] },
  },

  // Housekeeping
  {
    resourceType: 'Task',
    id: 'task-7',
    status: 'draft',
    intent: 'order',
    priority: 'routine',
    description: 'Update license — renewal due this month',
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(0),
    restriction: {
      period: {
        end: daysFromNow(14),
      },
    },
    code: { coding: [{ code: 'housekeeping', display: 'Housekeeping' }] },
  },

  // Team tasks (assigned to others) — note/signature focused
  {
    resourceType: 'Task',
    id: 'task-8',
    status: 'in-progress',
    intent: 'order',
    priority: 'asap',
    description: 'Progress note missing — Jane Smith',
    for: { reference: 'Patient/patient-2', display: 'Jane Smith' },
    owner: { reference: 'Practitioner/practitioner-2', display: 'Dr. Michael Chen' },
    authoredOn: daysFromNow(-2),
    restriction: {
      period: {
        end: daysFromNow(1),
      },
    },
    code: { coding: [{ code: 'missing-note', display: 'Missing Note' }] },
  },
  {
    resourceType: 'Task',
    id: 'task-9',
    status: 'requested',
    intent: 'order',
    priority: 'routine',
    description: 'Sign discharge summary — Alex Johnson',
    for: { reference: 'Patient/patient-3', display: 'Alex Johnson' },
    owner: { reference: 'Practitioner/practitioner-3', display: 'Jennifer Williams, LCSW' },
    authoredOn: daysFromNow(-1),
    restriction: {
      period: {
        end: daysFromNow(5),
      },
    },
    code: { coding: [{ code: 'signature', display: 'Signature' }] },
  },
  {
    resourceType: 'Task',
    id: 'task-10',
    status: 'accepted',
    intent: 'order',
    priority: 'urgent',
    description: 'Progress note missing signature — Maria Garcia',
    for: { reference: 'Patient/patient-4', display: 'Maria Garcia' },
    owner: { reference: 'Practitioner/practitioner-4', display: 'Robert Martinez, LPC' },
    authoredOn: daysFromNow(0),
    restriction: {
      period: {
        end: daysFromNow(0),
      },
    },
    code: { coding: [{ code: 'signature', display: 'Signature' }] },
  },

  // Completed
  {
    resourceType: 'Task',
    id: 'task-11',
    status: 'completed',
    intent: 'order',
    priority: 'routine',
    description: 'Sign progress note — John Doe (2/10)',
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(-7),
    restriction: {
      period: {
        end: daysFromNow(-3),
      },
    },
    code: { coding: [{ code: 'signature', display: 'Signature' }] },
  },
  {
    resourceType: 'Task',
    id: 'task-12',
    status: 'completed',
    intent: 'order',
    priority: 'urgent',
    description: 'Progress note missing — David Wilson (completed)',
    for: { reference: 'Patient/patient-5', display: 'David Wilson' },
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(-10),
    restriction: {
      period: {
        end: daysFromNow(-5),
      },
    },
    code: { coding: [{ code: 'missing-note', display: 'Missing Note' }] },
  },

  // Cancelled
  {
    resourceType: 'Task',
    id: 'task-13',
    status: 'cancelled',
    intent: 'order',
    priority: 'routine',
    description: 'Sign treatment plan — duplicate request cancelled',
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(-4),
    restriction: {
      period: {
        end: daysFromNow(-1),
      },
    },
    code: { coding: [{ code: 'other', display: 'Other' }] },
  },

  // On-hold
  {
    resourceType: 'Task',
    id: 'task-14',
    status: 'on-hold',
    intent: 'order',
    priority: 'urgent',
    description: 'Progress note missing — John Doe (awaiting chart correction)',
    for: { reference: 'Patient/patient-1', display: 'John Doe' },
    owner: { reference: 'Practitioner/current-user', display: 'Dr. Sarah Johnson' },
    authoredOn: daysFromNow(-6),
    restriction: {
      period: {
        end: daysFromNow(7),
      },
    },
    note: [{ text: 'Waiting for admin to fix encounter date in EHR.' }],
    code: { coding: [{ code: 'missing-note', display: 'Missing Note' }] },
  },
];

/**
 * Get tasks for the current user (My Tasks view)
 */
export function getMyTasks(): Task[] {
  return mockTasks.filter((task) => task.owner?.reference === currentUserRef);
}

/**
 * Get tasks for the team (Team Tasks view)
 */
export function getTeamTasks(): Task[] {
  return mockTasks.filter((task) => task.owner?.reference !== currentUserRef);
}

/**
 * Get all tasks
 */
export function getAllTasks(): Task[] {
  return mockTasks;
}
